export function productPagePath(productId) {
  return `/products/${productId}`;
}

export const ENTERPRISE_PRODUCT_IDS = ['sutramq', 'sutraacs', 'mgopennms'];

const ASSET_V = '6';

/** Rich page content for enterprise product detail pages */
export const PRODUCT_PAGES = {
  sutramq: {
    id: 'sutramq',
    name: 'SutraMQ',
    eyebrow: 'IoT & messaging platform',
    headline: 'Enterprise MQTT built for scale, security, and sovereignty',
    subheadline:
      'A high-performance MQTT broker engineered in Rust — designed for millions of connected devices, strict compliance, and production-grade observability.',
    heroImage: `/images/hero/sutramq.svg?v=${ASSET_V}`,
    handcrafted: true,
    accent: '#3b82f6',
    accentGlow: 'rgba(59, 130, 246, 0.35)',
    overview: [
      'SutraMQ delivers the throughput and reliability enterprises expect from commercial MQTT platforms — with built-in privacy scanning, regional compliance controls, and clustering for always-on IoT and industrial workloads.',
      'From smart cities and manufacturing to telecom and energy, SutraMQ keeps device traffic secure, observable, and under your operational control.',
    ],
    audiences: ['IoT platform teams', 'Industrial automation', 'Telecom & utilities', 'SaaS device backends'],
    features: [
      {
        title: 'MQTT 3.1.1 & 5.0',
        description: 'Full protocol support with WebSocket transport for browsers and edge gateways.',
      },
      {
        title: 'Massive scale',
        description: 'Architecture targeting 1M+ concurrent connections with efficient topic routing.',
      },
      {
        title: 'Clustering & HA',
        description: 'Raft-based clustering with gossip coordination for horizontal scale and failover.',
      },
      {
        title: 'Security first',
        description: 'TLS 1.2+, mutual TLS, Argon2 authentication, ACL engine with wildcard topics.',
      },
      {
        title: 'PII & compliance',
        description: 'Built-in payload scanning across regions — privacy by design for regulated deployments.',
      },
      {
        title: 'Operations ready',
        description: 'Prometheus metrics, structured logging, management API, and rate limiting per client.',
      },
    ],
    modules: [
      { name: 'Connection handler', detail: 'MQTT TCP/TLS, WebSocket, session management' },
      { name: 'Topic router', detail: 'Trie-based O(d) matching for high fan-out' },
      { name: 'Auth & ACL', detail: 'Credentials, mTLS identity, fine-grained publish/subscribe rules' },
      { name: 'Cluster layer', detail: 'Multi-node coordination with bridge integrations' },
      { name: 'Management API', detail: 'Runtime stats, client inspection, operational controls' },
    ],
    useCases: [
      {
        title: 'Smart infrastructure',
        text: 'Connect sensors, actuators, and gateways across campuses with predictable latency and audit trails.',
      },
      {
        title: 'Telecom & CPE telemetry',
        text: 'Ingest device events at carrier scale alongside SutraACS and MGOpen NMS in a unified Mahageeta stack.',
      },
      {
        title: 'Regulated industries',
        text: 'Deploy with regional privacy controls and PII scanning for healthcare, finance, and public sector IoT.',
      },
    ],
    specs: [
      ['Protocol', 'MQTT 3.1.1, MQTT 5.0, WebSocket'],
      ['Connections', '1M+ concurrent (clustered)'],
      ['Security', 'TLS, mTLS, ACL, rate limits'],
      ['Compliance', 'Regional PII scanning'],
      ['Observability', 'Prometheus, structured logs'],
      ['Engine', 'Rust — memory-safe, high throughput'],
    ],
  },
  sutraacs: {
    id: 'sutraacs',
    name: 'SutraACS',
    eyebrow: 'Telecom & CPE platform',
    headline: 'TR-069 ACS for CPE fleets at carrier scale',
    subheadline:
      'Auto Configuration Server built for ISPs, OEMs, and managed service providers — secure provisioning, firmware delivery, and full operator control.',
    heroImage: `/images/hero/sutraacs.svg?v=${ASSET_V}`,
    handcrafted: true,
    accent: '#10b981',
    accentGlow: 'rgba(16, 185, 129, 0.35)',
    overview: [
      'SutraACS is a modern TR-069 / CWMP platform for managing routers, gateways, and CPE at millions of devices. It combines a protocol-native core with an operator portal and northbound APIs your OSS/BSS can integrate with.',
      'Engineered for sovereignty and security — deploy on your cloud or data centre with the same discipline we apply across the Mahageeta product portfolio.',
    ],
    audiences: ['ISPs & broadband providers', 'CPE OEMs', 'Managed service providers', 'Enterprise WAN teams'],
    features: [
      {
        title: 'TR-069 / CWMP',
        description: 'Standards-compliant session handling, Inform events, and parameter model management.',
      },
      {
        title: 'Firmware & config',
        description: 'Push firmware images and configuration profiles with staged rollouts and rollback.',
      },
      {
        title: 'Operator portal',
        description: 'Web console for device search, provisioning jobs, alarms, and day-two operations.',
      },
      {
        title: 'Northbound API',
        description: 'REST NBI for integration with billing, CRM, and existing network orchestration.',
      },
      {
        title: 'Provisioning engine',
        description: 'Flexible policy scripts for zero-touch provisioning and lifecycle automation.',
      },
      {
        title: 'Fleet visibility',
        description: 'Live device status, session history, and audit logs across the entire CPE estate.',
      },
    ],
    modules: [
      { name: 'CWMP server', detail: 'TR-069 protocol engine and session lifecycle' },
      { name: 'File server', detail: 'Firmware and config file delivery to CPE' },
      { name: 'Northbound API', detail: 'REST integration layer for external systems' },
      { name: 'Operator portal', detail: 'Dashboard for provisioning, monitoring, and support' },
      { name: 'Provisioning engine', detail: 'Policy-driven automation for device onboarding' },
    ],
    useCases: [
      {
        title: 'Broadband rollouts',
        text: 'Zero-touch provisioning for new subscriber CPE with firmware policies and remote diagnostics.',
      },
      {
        title: 'OEM lifecycle management',
        text: 'Manage firmware channels, parameter templates, and field upgrades across product SKUs.',
      },
      {
        title: 'MSP managed CPE',
        text: 'Operate customer-premises equipment at scale with role-based access and API automation.',
      },
    ],
    specs: [
      ['Standard', 'TR-069 / CWMP'],
      ['Scale', 'Millions of CPE sessions'],
      ['Delivery', 'Firmware & config push'],
      ['Integration', 'Northbound REST API'],
      ['Console', 'Operator web portal'],
      ['Engine', 'Rust core + modern services'],
    ],
  },
  mgopennms: {
    id: 'mgopennms',
    name: 'MGOpen NMS',
    eyebrow: 'Network operations platform',
    headline: 'Unified network & infrastructure management',
    subheadline:
      'Microservices-based NMS for networking, telecom, OEMs, and MSPs — with web portal, AI ops, and mobile apps for field teams.',
    heroImage: `/images/hero/mgopennms.svg?v=${ASSET_V}`,
    handcrafted: true,
    accent: '#8b5cf6',
    accentGlow: 'rgba(139, 92, 246, 0.35)',
    overview: [
      'MGOpen NMS brings inventory, topology, alerting, configuration, and AI-assisted operations into one platform. Ten focused microservices scale independently while the operator portal and mobile apps give teams a single pane of glass.',
      'Built for teams who outgrow spreadsheets and siloed tools — from device discovery through SLA reporting and root-cause analysis.',
    ],
    audiences: ['Network operators', 'Telecom NOC teams', 'MSPs & integrators', 'Enterprise IT infrastructure'],
    features: [
      {
        title: 'Device inventory',
        description: 'Multi-protocol inventory with search, filters, and rich metadata across vendors.',
      },
      {
        title: 'Topology mapping',
        description: 'LLDP, CDP, and BGP-aware maps for visual fault isolation and capacity planning.',
      },
      {
        title: 'Alert management',
        description: 'Severity-based alerting with acknowledge, resolve workflows, and notification rules.',
      },
      {
        title: 'Config backup',
        description: 'Scheduled backups, diff/compare, and restore for change control and compliance.',
      },
      {
        title: 'AI Ops assistant',
        description: 'Natural-language queries, anomaly detection, and guided root-cause analysis.',
      },
      {
        title: 'Mobile apps',
        description: 'iOS and Android apps for alerts, devices, and field operations on the go.',
      },
    ],
    modules: [
      { name: 'API gateway', detail: 'Unified entry point for portal and integrations' },
      { name: 'Device service', detail: 'Inventory, polling, and multi-protocol support' },
      { name: 'Topology service', detail: 'Graph construction and map visualisation' },
      { name: 'Alerting service', detail: 'Rules, escalation, and notification delivery' },
      { name: 'AI Ops engine', detail: 'ML-assisted diagnostics and conversational ops' },
      { name: 'Reports & SLA', detail: 'Capacity, uptime, and exportable compliance reports' },
    ],
    useCases: [
      {
        title: 'NOC modernisation',
        text: 'Replace legacy NMS silos with a unified dashboard, topology, and AI-assisted triage.',
      },
      {
        title: 'MSP multi-tenant ops',
        text: 'Manage customer networks with RBAC, per-tenant views, and automated reporting.',
      },
      {
        title: 'Field + central ops',
        text: 'Sync central NOC workflows with mobile apps for technicians on site.',
      },
    ],
    specs: [
      ['Architecture', '10 microservices + gateway'],
      ['Portal', 'Full web operator console'],
      ['Mobile', 'iOS & Android (Expo)'],
      ['Discovery', 'Automated device onboarding jobs'],
      ['AI', 'Anomaly detection & NL assistant'],
      ['Reports', 'SLA, capacity, exports'],
    ],
  },
};
